import { useMutation } from '@apollo/client';
import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildObligationInput,
  UpdateObligationMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateObligationDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateObligationTRPC } from './useUpdateObligationTRPC';

export const useUpdateObligation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateObligationGraphQL, graphqlState] = useMutation(
    UpdateObligationDocument,
    {
      update: (cache) => evictField(cache, 'obligation'),
      refetchQueries: [namedOperations.Query.getObligationById],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateObligationTRPC();

  const updateObligation = async (
    variables: UpdateChildObligationInput
  ): Promise<UpdateObligationMutation> => {
    if (trpcEnabled) {
      const { schedule, ...rest } = variables;

      return trpcMutation.updateObligation({
        ...rest,
        ...mapScheduleToTRPC(schedule),
      });
    }

    const result = await updateObligationGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update obligation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateObligation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateObligation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
